import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { GetApp as GetAppIcon } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { AppDataGridColumnSettingsButton, JobIdAutocomplete, OrderIdAutocomplete, Page, ReportTable } from 'components';
import { useSession } from 'store';
import { t } from 'i18n';
import { callApi, download } from 'utils';
import { allTrackers } from 'consts';

const tinkTracker = 'tink';
const trackerOptions = [
  { value: tinkTracker, label: 'Tink' },
  ...allTrackers.map((label, idx) => ({ value: String(idx), label })),
];
const formatTracker = (val) => val === tinkTracker ? 'Tink' : allTrackers[Number(val)];
const formatZeroAsDash = (val) => Number(val) === 0 ? '-' : val;
const idColumn = { minWidth: 64, flex: 0.45 };
const metricColumn = { numeric: true, minWidth: 76, flex: 0.55 };
const rateColumn = { numeric: true, minWidth: 80, flex: 0.6 };
const jumpColumn = { numeric: true, minWidth: 68, flex: 0.5 };

const columns = [
  { name: 'date', label: '日期', minWidth: 104, flex: 0.75 },
  { name: 'parent', label: '订单ID', ...idColumn },
  { name: 'id', label: '任务ID', ...idColumn },
  { name: 'job_name', label: '任务名称', truncate: true, minWidth: 220, flex: 3 },
  { name: 'tracker', label: '检测公司', format: formatTracker, minWidth: 88, flex: 0.7 },
  { name: 'stay', label: '停留时间', ...metricColumn },
  { name: 'landing', label: '到达次数', ...metricColumn },
  { name: 'landingRate', label: '到达率', ...rateColumn },
  { name: 'cookieClicks', label: 'Cookie点击数量', format: formatZeroAsDash, minWidth: 104, flex: 0.75, numeric: true },
  { name: 'cookieClickRate', label: 'Cookie点击率', format: formatZeroAsDash, minWidth: 96, flex: 0.7, numeric: true },
  { name: 'bounceRate', label: '跳出率', ...rateColumn },
  { name: 'jump2', label: '二跳', format: formatZeroAsDash, ...jumpColumn },
  { name: 'jump2Rate', label: '二跳率', format: formatZeroAsDash, ...rateColumn },
  { name: 'jump3', label: '三跳', format: formatZeroAsDash, ...jumpColumn },
  { name: 'jump3Rate', label: '三跳率', format: formatZeroAsDash, ...rateColumn },
  //{ name: 'jiagou', label: '加购数' },
];
const columnMap = Object.fromEntries(columns.map(col => [col.name, col]));

const defaultFilter = {idtype: 'parent', id: '', orderId: '', jobId: '', tracker: '', start_date: null, end_date: null};
const defaultLimit = 100;
const getDefaultDateRange = () => {
  const endDate = dayjs();
  return {
    start_date: endDate.subtract(6, 'day'),
    end_date: endDate,
  };
};
const getDefaultFilter = () => ({
  ...defaultFilter,
  ...getDefaultDateRange(),
});
const buildTrackerReportParams = (searchParams, {pagination=true} = {}) => {
  const params = new URLSearchParams(searchParams);
  const defaultDateRange = getDefaultDateRange();

  params.get('start') || params.set('start', defaultDateRange.start_date.format('YYYY-MM-DD'));
  params.get('end') || params.set('end', defaultDateRange.end_date.format('YYYY-MM-DD'));
  if (pagination) {
    params.set('limit', defaultLimit);
  } else {
    params.delete('page');
    params.delete('limit');
  }

  return params;
};
const filterItemSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
};
const filterToolbarSx = {
  width: '100%',
  flexWrap: 'wrap',
};
const textFilterItemSx = {
  ...filterItemSx,
  flex: {xs: '1 1 100%', sm: '1 1 210px', md: '1 1 170px'},
  minWidth: {xs: '100%', sm: 160},
};
const dateFilterItemSx = {
  ...filterItemSx,
  flex: {xs: '1 1 100%', sm: '1.2 1 300px'},
  minWidth: {xs: '100%', sm: 300},
};
const filterActionsSx = {
  display: 'flex',
  flex: {xs: '1 1 100%', sm: '0 0 auto'},
  gap: 1,
  whiteSpace: 'nowrap',
};
const filterControlSx = {
  p: 0,
  flex: '1 1 130px',
  minWidth: 0,
};
const datePickerSx = {
  flex: '1 1 120px',
  minWidth: 0,
  '& .MuiInputBase-input': {p: '6px'},
};
const filterButtonSx = {
  minWidth: 88,
  flexShrink: 0,
};
function formatCellValue(col, row) {
  const column = columnMap[col];
  return column?.format ? column.format(row[col]) : row[col];
}

const TrackerReports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportGridApiRef = useGridApiRef();
  const session = useSession((state) => state.session);
  const [data, setData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState(null);
  const activeRequestRef = useRef('');
  const inFlightRequestsRef = useRef(new Set());
  const [filter, setFilter] = useState(() => {
    const defaultDateRange = getDefaultDateRange();
    const nextFilter = Object.fromEntries(Object.keys(defaultFilter).map(k => {
      let v;
      if (k.endsWith('_date')) {
        v = searchParams.get(k.slice(0, -5));
        v = v ? dayjs(v) : defaultDateRange[k];
      } else {
        v = searchParams.get(k) || defaultFilter[k];
      }
      return [k, v];
    }));
    if (nextFilter.id) {
      if (nextFilter.idtype === 'id') {
        nextFilter.jobId = nextFilter.id;
      } else {
        nextFilter.orderId = nextFilter.id;
      }
    }
    return nextFilter;
  });

  const page = searchParams.get('page')|0;
  const limit = defaultLimit;

  useEffect(() => {
    if (!searchParams.has('limit')) return;
    setSearchParams((prev) => {
      prev.delete('limit');
      return prev;
    });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const params = buildTrackerReportParams(searchParams);
    const requestKey = params.toString();
    activeRequestRef.current = requestKey;
    if (inFlightRequestsRef.current.has(requestKey)) return;

    inFlightRequestsRef.current.add(requestKey);
    callApi(`/reports/trackers?${requestKey}`)
      .then((res) => {
        if (activeRequestRef.current === requestKey) {
          setData(res);
        }
      })
      .catch(console.error)
      .finally(() => {
        inFlightRequestsRef.current.delete(requestKey);
      });
  }, [refreshKey, searchParams]);

  const handleFilter = useCallback((ev) => setFilter(prev => ({...prev, [ev.target.name]: ev.target.value})), []);
  const handleOrderIdFilter = useCallback((ev) => {
    const orderId = ev.target.value;
    setFilter(prev => ({
      ...prev,
      idtype: orderId ? 'parent' : (prev.jobId ? 'id' : 'parent'),
      id: orderId || prev.jobId || '',
      orderId,
      jobId: orderId ? '' : prev.jobId,
    }));
  }, []);
  const handleJobIdFilter = useCallback((ev) => {
    const jobId = ev.target.value;
    setFilter(prev => ({
      ...prev,
      idtype: jobId ? 'id' : 'parent',
      id: jobId || prev.orderId || '',
      jobId,
    }));
  }, []);
  const changeDate = (name, val) => setFilter(prev => ({...prev, [name]: val}));
  const submitFilter = (ev) => {
    ev?.preventDefault();
    const prev = new URLSearchParams(searchParams);
    const hasPagination = prev.has('page') || prev.has('limit');
    prev.delete('page');
    prev.delete('limit');

    const sp = new URLSearchParams();
    const id = filter.jobId || filter.orderId || filter.id;
    if (id) {
      sp.set('idtype', filter.jobId ? 'id' : (filter.orderId ? 'parent' : filter.idtype));
      sp.set('id', id.trim());
    }
    filter.tracker !== '' && sp.set('tracker', filter.tracker);
    filter.start_date && sp.set('start', filter.start_date.format('YYYY-MM-DD'));
    filter.end_date && sp.set('end', filter.end_date.format('YYYY-MM-DD'));

    if (hasPagination || prev.toString() !== sp.toString()) {
      setSearchParams(sp);
    } else {
      setRefreshKey((key) => key + 1);
    }
  };
  const resetFilter = () => setFilter(getDefaultFilter());

  const handlePagination = (k, v) => {
    setSearchParams((prev) => {
      if (k === 'page') {
        v > 0 ? prev.set(k, v) : prev.delete(k);
      } else {
        v !== defaultLimit ? prev.set(k, v) : prev.delete(k);
        prev.delete('page');
      }
      return prev;
    });
  };
  const exportTrackerReport = () => {
    const params = buildTrackerReportParams(searchParams, {pagination: false});
    download(`/reports/export?type=trackers&${params.toString()}`, '后端数据');
  };

  return (
    <Page title='后端数据' styles={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <Stack spacing={1} sx={{height: '100%', minHeight: 0, p: 2, pt: 1}}>
        <Box component="form" onSubmit={submitFilter} sx={{flexShrink: 0}}>
          <Grid container spacing={1} alignItems="center" sx={filterToolbarSx}>
            <Grid item sx={textFilterItemSx}>
              <Typography sx={{mr: 0.5, flexShrink: 0}}>订单:</Typography>
              <OrderIdAutocomplete
                sx={filterControlSx}
                name="id"
                value={filter.orderId}
                onChange={handleOrderIdFilter}
              />
            </Grid>
            <Grid item sx={textFilterItemSx}>
              <Typography sx={{mr: 0.5, flexShrink: 0}}>任务:</Typography>
              <JobIdAutocomplete
                groupId={filter.orderId}
                sx={filterControlSx}
                name="id"
                value={filter.jobId}
                onChange={handleJobIdFilter}
              />
            </Grid>
            <Grid item sx={textFilterItemSx}>
              <Typography sx={{mr: 0.5, flexShrink: 0}}>检测公司:</Typography>
              <Select
                name="tracker"
                value={filter.tracker}
                size="small"
                onChange={handleFilter}
                displayEmpty
                sx={{
                  ...filterControlSx,
                  '& .MuiSelect-select': {py: '6px'},
                }}
              >
                <MenuItem value={''}>--请选择--</MenuItem>
                {trackerOptions.map(({value, label}) => (
                  <MenuItem value={value} key={value}>{label}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item sx={dateFilterItemSx}>
              <Typography sx={{mr: 0.5, flexShrink: 0}}>排期时间:</Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0}}>
                <DatePicker
                  sx={datePickerSx}
                  value={filter.start_date}
                  views={['year', 'month', 'day']}
                  onChange={val => changeDate('start_date', val)}
                  dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                />
                <Typography component="span" sx={{flexShrink: 0}}>~</Typography>
                <DatePicker
                  sx={datePickerSx}
                  value={filter.end_date}
                  views={['year', 'month', 'day']}
                  minDate={filter.start_date}
                  onChange={val => changeDate('end_date', val)}
                  dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                />
              </Box>
            </Grid>
            <Grid item sx={filterActionsSx}>
              <Button
                type="submit"
                sx={filterButtonSx}
                variant="outlined"
                size="small"
              >搜索</Button>
              <Button
                type="button"
                sx={filterButtonSx}
                variant="outlined"
                size="small"
                onClick={resetFilter}
              >重置</Button>
              <Button
                type="button"
                sx={filterButtonSx}
                variant="outlined"
                size="small"
                startIcon={<GetAppIcon />}
                onClick={exportTrackerReport}
              >下载</Button>
              <AppDataGridColumnSettingsButton
                apiRef={reportGridApiRef}
                buttonId="tracker-report-column-settings-button"
                panelId="tracker-report-column-settings-panel"
                label={t('Column Settings')}
                ref={setColumnSettingsAnchorEl}
                type="button"
              />
            </Grid>
          </Grid>
        </Box>
        <ReportTable
          apiRef={reportGridApiRef}
          data={data}
          columns={columnMap}
          getColumnLabel={(col) => data?.columnLabels?.[col] || columnMap[col]?.label || col}
          renderCell={formatCellValue}
          getCellTitle={(col, row) => row[col]}
          getRowKey={(row) => `${row.date}:${row.id}:${row.tracker}`}
          page={page}
          rowsPerPage={limit}
          onPagination={handlePagination}
          showColumnSettings
          columnSettingsTableId="report-trackers"
          columnSettingsUserId={session?.uid}
          columnSettingsAnchorEl={columnSettingsAnchorEl}
        />
      </Stack>
    </Page>
  );
};

export default TrackerReports;
